import "./styles.css";

import { Button, Input } from "@mui/material";
import React, { useEffect, useState } from "react";

import AudioWrapV2 from "../audioWrap/audioWrapV2";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";

/**
 * Component for handling file uploads with support for images, videos, and audio.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.label - The label for the file input.
 * @param {Object} props.schema - The schema for configuring file upload behavior.
 * @param {function} props.handleChange - Function to handle changes in the files.
 * @returns {JSX.Element} - Rendered component.
 */
const FilesUploadModel = ({ label, schema, handleChange }) => {
  const [isVideoOn, setVideoOn] = useState(false);
  const [isAudioOn, setAudioOn] = useState(false);
  const [cameraShow, setCameraShow] = useState(false);
  const [videoShow, setVideoShow] = useState(false);
  const [audioShow, setAudioShow] = useState(false);
  const [fileShow, setFileShow] = useState(false);
  const [extraInfo, setExtraInfo] = useState("");

  const toggleVideo = () => {
    setVideoOn((prevVidState) => !prevVidState);
  };

  const toggleAudio = () => {
    setAudioOn((prevAudState) => !prevAudState);
  };

  const handleCameraButtonClick = () => {
    document.getElementById(`cameraInput_${schema?.path}`).click();
  };

  const handleVideoButtonClick = () => {
    document.getElementById(`videoInput_${schema?.path}`).click();
  }

  useEffect(() => {
    if (schema?.media === "camera") {
      setCameraShow(true);
    }
    if (schema?.media === "video") {
      setVideoShow(true);
    }
    if (schema?.media === "audio") {
      setAudioShow(true);
    }
    if (schema?.media === "file") {
      setFileShow(true);
    }
    setExtraInfo(schema?.extraInfo);
  }, [schema]);

  const handleFileInputChange = (event) => {
    const selectedFiles = event.target.files;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const fileName = file.name;
      const fileExtension = fileName.split(".").pop();
      const uniqueName = `${fileName.replace(
        /\.[^/.]+$/,
        ""
      )}_${Date.now()}.${fileExtension}`;
      const uniqueFile = new File([file], uniqueName, { type: file.type });
      // structure of the object over here should be {file: fileObject, metadata: metadataObect}
      const fileObject = {
        file: uniqueFile,
        metadata: {
          type: uniqueFile.type,
          size: uniqueFile.size,
        },
      };
      handleChange(fileObject);
    }
  };

  return (
    <div className="filesUploadModel" id={`${schema?.path}`}>
      {isAudioOn && (
        <AudioWrapV2
          {...{ handleChange, isAudioOn, toggleAudio, extraInfo, label }}
        />
      )}
      <div className="heading">
        {label}
        {schema?.file_required && <span>*</span>}
      </div>
      {extraInfo && (
        <div className="subheading">
          {extraInfo?.map((info, index) => (
            <p key={index}>{info}</p>
          ))}
        </div>
      )}

      <div>
        {cameraShow && (
          <>
            <Button
              onClick={handleCameraButtonClick}
              variant="contained"
              color="primary"
              startIcon={<CameraAltIcon />}
              style={{
                width: "100%",
                textTransform: "none",
                marginBottom: "10px",
              }}
            >
              Capture Photo
            </Button>
            <Input
              type="file"
              inputProps={{ accept: "image/*", capture: "environment" }}
              onChange={handleFileInputChange}
              id={`cameraInput_${schema?.path}`}
              style={{ display: "none" }}
            />
          </>
        )}
        {fileShow && (
          <>
            <Button
              onClick={handleCameraButtonClick}
              variant="contained"
              color="primary"
              startIcon={<FileUploadIcon />}
              style={{
                width: "100%",
                textTransform: "none",
                marginBottom: "10px",
              }}
            >
              Upload Files
            </Button>
            <Input
              type="file"
              inputProps={{ accept: "image/*", multiple: true }}
              onChange={handleFileInputChange}
              id={`cameraInput_${schema?.path}`}
              style={{ display: "none" }}
            />
          </>
        )}
        {videoShow &&
          <>
            <Button
              onClick={handleVideoButtonClick}
              variant="contained"
              color="primary"
              startIcon={<VideocamIcon />}
              style={{
                width: "100%",
                textTransform: "none",
                marginBottom: "10px",
              }}
            >
              Capture Video
            </Button>
            <Input
              type="file"
              inputProps={{ accept: "video/*", capture: "environment" }}
              onChange={handleFileInputChange}
              id={`videoInput_${schema?.path}`}
              style={{ display: "none" }}
            />
          </>
        }

        {audioShow && (
          <Button
            onClick={toggleAudio}
            variant="contained"
            color="primary"
            startIcon={<MicIcon />}
            style={{ width: "100%", textTransform: "none" }}
          >
            Record Audio
          </Button>
        )}
      </div>
    </div>
  );
};

export default FilesUploadModel;
